import * as React from "react";
import { Avatar as AvatarPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

// Radix tracks load status via a detached probe Image, so its context can get
// stuck at "loaded" after we blank the src (a queued probe `load` event fires
// last), leaving a src-less <img> and a permanently hidden Fallback. This
// context carries the real "image is blank" truth between Image and Fallback.
const AvatarImageBlankContext = React.createContext(false);
const AvatarSetImageBlankContext = React.createContext<(blank: boolean) => void>(() => {});

function Avatar({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: "default" | "sm" | "lg";
}) {
  const [imageBlank, setImageBlank] = React.useState(false);
  return (
    <AvatarImageBlankContext.Provider value={imageBlank}>
      <AvatarSetImageBlankContext.Provider value={setImageBlank}>
        <AvatarPrimitive.Root
          data-slot="avatar"
          data-size={size}
          className={cn(
            "group/avatar relative flex size-8 shrink-0 overflow-hidden rounded-full select-none data-[size=lg]:size-10 data-[size=sm]:size-6",
            className,
          )}
          {...props}
        />
      </AvatarSetImageBlankContext.Provider>
    </AvatarImageBlankContext.Provider>
  );
}

function AvatarImage({
  className,
  src,
  onLoad,
  onError,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  // Broken images must always yield the Fallback, never the browser's broken
  // glyph or gray bands. Three failure paths, each needing its own catch:
  // load-then-undecodable (truncated body — only decode() rejects), a plain
  // error event, and an error that fired BEFORE React hydrated its handlers
  // (SSR gap — detected post-mount via complete + naturalWidth === 0).
  const [broken, setBroken] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const setImageBlank = React.useContext(AvatarSetImageBlankContext);
  const blank = broken || !src;
  React.useEffect(() => setBroken(false), [src]);
  React.useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth === 0) setBroken(true);
  }, [src]);
  React.useEffect(() => {
    setImageBlank(blank);
    return () => setImageBlank(false);
  }, [blank, setImageBlank]);
  if (blank) return null;
  return (
    <AvatarPrimitive.Image
      ref={imgRef}
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      src={src}
      onLoad={(e) => {
        onLoad?.(e);
        e.currentTarget.decode().catch(() => setBroken(true));
      }}
      onError={(e) => {
        onError?.(e);
        setBroken(true);
      }}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  const imageBlank = React.useContext(AvatarImageBlankContext);
  const fallbackClassName = cn(
    "flex size-full items-center justify-center rounded-full bg-muted text-sm text-muted-foreground group-data-[size=sm]/avatar:text-xs",
    className,
  );
  if (imageBlank) {
    const { delayMs: _delayMs, ...spanProps } = props;
    return <span data-slot="avatar-fallback" className={fallbackClassName} {...spanProps} />;
  }
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={fallbackClassName}
      {...props}
    />
  );
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background select-none",
        "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden",
        "group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2",
        "group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2",
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background",
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "relative flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground ring-2 ring-background group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3",
        className,
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback, AvatarBadge, AvatarGroup, AvatarGroupCount };
